import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { timelineApi } from 'api/timeline';
import { usePanel } from 'contexts/panelContext';
import { useTranslation } from 'react-i18next';
import { TimelineEventItem } from 'types/Timeline';
import { DialogConfirm } from 'components/_new/Dialog';
import { Button, ButtonProps } from 'components/_new/Button';

type TimelineEventCardActionsProps = {
	data: TimelineEventItem;
	onUpdate?: (data: TimelineEventItem) => void;
	actions?: Array<ButtonProps & { key: string }>;
};

export const TimelineEventCardActions = ({ data, onUpdate, actions }: TimelineEventCardActionsProps) => {
	const { t } = useTranslation();
	const dateIsBefore = dayjs().isBefore(data?.endAt || data?.dayDate);
	const withActions = [data.requireConfirmation].some(Boolean) || (actions || [])?.length > 0;

	const { currentApplicationData } = usePanel();

	const [confirmParticipationDialogOpen, setConfirmParticipationDialogOpen] = useState(false);
	const confirmParticipation = () => {
		// nope
	};

	const [cancelParticipationDialogOpen, setCancelParticipationDialogOpen] = useState(false);
	const cancelParticipation = () => {
		// nope
	};

	const { mutate: confirmParticipationMutate, isLoading: confirmParticipationLoading } = useMutation(
		() => timelineApi.confirmParticipation(Number(currentApplicationData.id), Number(data.eventId)),
		{
			onSuccess: (data) => {
				onUpdate?.(data);
				setConfirmParticipationDialogOpen(false);
			},
		}
	);

	const { mutate: cancelParticlipationMutate, isLoading: cancelarticipationLoading } = useMutation(
		() => timelineApi.cancelParticlipation(Number(currentApplicationData.id), Number(data.eventId)),
		{
			onSuccess: (data) => {
				onUpdate?.(data);
				setCancelParticipationDialogOpen(false);
			},
		}
	);

	const confirmParticipationDisabled = Boolean(
		data.requireConfirmation && data.participantLimit && (data.currentParticipants || 0) >= data.participantLimit
	);

	return withActions ? (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			<DialogConfirm
				visible={confirmParticipationDialogOpen}
				onHide={() => {
					setConfirmParticipationDialogOpen(false);
				}}
				message={t('timeline.confirmParticipationMessage')}
				confirmAction={{
					label: t('misc.yes'),
					onClick: confirmParticipation,
					loading: confirmParticipationLoading,
				}}
				onConfirm={confirmParticipationMutate}
				rejectAction={{ label: t('misc.no') }}
			/>
			<DialogConfirm
				visible={cancelParticipationDialogOpen}
				onHide={() => {
					setCancelParticipationDialogOpen(false);
				}}
				message={t('timeline.cancelParticipationMessage')}
				confirmAction={{
					label: t('timeline.action.cancelParticipation'),
					onClick: cancelParticipation,
					loading: cancelarticipationLoading,
				}}
				onConfirm={cancelParticlipationMutate}
				rejectAction={{ label: t('actions.close') }}
			/>
			<div className="flex flex-row gap-4 justify-content-start align-items-start sm:justify-content-end sm:align-items-end">
				{!actions?.length &&
					data.eventId &&
					data.requireConfirmation &&
					dateIsBefore &&
					(data.attendanceConfirmed ? (
						<Button
							icon="check"
							label={t('timeline.item.attendanceConfirmed')}
							variant="gray-success"
							rounded
							variantSize="sm"
							onClick={() => {
								setCancelParticipationDialogOpen(true);
							}}
						/>
					) : (
						<Button
							icon="user-plus"
							label={
								// confirmParticipationDisabled
								// 	? t('timeline.participationLimitReached')
								// 	: t('timeline.confirmParticipation')
								t('timeline.confirmParticipation')
							}
							variant="rose"
							rounded
							variantSize="sm"
							onClick={() => {
								setConfirmParticipationDialogOpen(true);
							}}
							disabled={confirmParticipationDisabled}
							tooltip={confirmParticipationDisabled ? t('timeline.noAvailablePlacesForEvent') : undefined}
						/>
					))}

				{(actions || []).map(({ key, ...buttonProps }) => (
					<Button key={key} {...buttonProps} />
				))}
			</div>
		</>
	) : null;
};
